import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ComissaoService from "../../services/resource/ComissaoService";
import { formatarMoedaBrasileira } from "../../utils/formatarMoedaBrasileira";
import { formatLocalDateTime } from "../../utils/formatDate";

const FUNCOES = {
    MOTORISTA: "Motorista",
    AUXILIAR_ENTREGA: "Auxiliar de entrega",
};

const formatarData = (iso) =>
    iso ? String(iso).substring(0, 10).split("-").reverse().join("/") : "—";

/**
 * Comissões parciais já geradas na competência. Elas ficam BLOQUEADAS: nenhuma geração geral as recalcula nem as
 * sobrescreve - a geral só paga os dias que sobram. Para refazer uma, exclua-a aqui e gere novamente.
 */
export default function ComissoesParciaisBloqueadas({ ano, mes, refreshKey = 0, onAlterado }) {
    const comissaoService = useMemo(() => new ComissaoService(), []);
    const [lista, setLista] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [excluindoId, setExcluindoId] = useState(null);

    const carregar = () => {
        if (!ano || !mes) return;
        setCarregando(true);
        comissaoService.parciaisBloqueadas({ ano: Number(ano), mes: Number(mes) })
            .then(res => setLista(res.data ?? []))
            .catch(e => console.error(e))
            .finally(() => setCarregando(false));
    };

    useEffect(() => { carregar(); }, [ano, mes, refreshKey]);

    const excluir = async (item) => {
        const texto = `Excluir a comissão parcial de ${item.funcionarioNome} (${FUNCOES[item.tipoComissao] ?? "—"}, `
            + `${formatarData(item.periodoInicial)} a ${formatarData(item.periodoFinal)})?\n\n`
            + "Esses dias ficam livres para a geração geral, e a comissão geral do funcionário (se houver) será recalculada.";
        if (!window.confirm(texto)) return;

        setExcluindoId(item.id);
        try {
            await comissaoService.excluirParcial(item.id);
            toast.success("Comissão parcial excluída.");
            carregar();
            onAlterado?.();
        } catch (e) {
            console.error(e);
        } finally {
            setExcluindoId(null);
        }
    };

    return (
        <div className="rounded-2xl border border-border shadow-card bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    <i className="fa fa-lock mr-1" /> Comissões Parciais Bloqueadas
                </p>
                <span className="text-xs text-muted-foreground">
                    {mes}/{ano} · {lista.length} comissão(ões)
                </span>
            </div>

            <p className="mb-3 text-xs text-muted-foreground">
                Estas comissões foram geradas parcialmente e estão protegidas: a geração geral não as recalcula nem as
                sobrescreve; ela paga apenas os dias do funcionário que ainda não estão em uma parcial.
            </p>

            {carregando ? (
                <p className="py-4 text-center text-xs text-muted-foreground">
                    <i className="fa fa-spinner fa-spin mr-2" /> Carregando...
                </p>
            ) : lista.length === 0 ? (
                <p className="py-4 text-center text-xs text-muted-foreground">
                    Nenhuma comissão parcial bloqueada nesta competência.
                </p>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-xs">
                        <thead className="bg-muted/50 text-left text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2">Funcionário</th>
                                <th className="px-3 py-2">Tipo</th>
                                <th className="px-3 py-2">Período inicial</th>
                                <th className="px-3 py-2">Período final</th>
                                <th className="px-3 py-2 text-right">Valor</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2">Gerado por</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {lista.map(item => (
                                <tr key={item.id} className="border-t border-border">
                                    <td className="px-3 py-2">
                                        <p className="font-medium text-foreground">{item.funcionarioNome}</p>
                                        <p className="text-muted-foreground">{item.funcionarioCodigo} · {item.filialNome}</p>
                                    </td>
                                    <td className="px-3 py-2">{FUNCOES[item.tipoComissao] ?? "—"}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">{formatarData(item.periodoInicial)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">{formatarData(item.periodoFinal)}</td>
                                    <td className="px-3 py-2 text-right font-semibold text-foreground whitespace-nowrap">
                                        R$ {formatarMoedaBrasileira(item.valorTotalReceber)}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">
                                            <i className="fa fa-lock text-[10px]" /> {item.status === "FECHADA" ? "Fechada" : "Bloqueada"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        {item.geradoPor ?? "—"}
                                        {item.dataCalculo && <p className="text-muted-foreground">{formatLocalDateTime(item.dataCalculo)}</p>}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {item.podeExcluir && (
                                            <button
                                                type="button"
                                                onClick={() => excluir(item)}
                                                disabled={excluindoId === item.id}
                                                className="rounded-md px-2 py-1 text-xs font-medium text-destructive transition-colors duration-150 hover:bg-destructive/10 disabled:opacity-40"
                                            >
                                                {excluindoId === item.id ? "Excluindo..." : "Excluir"}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
